import { Select } from 'radix-ui';

import { IconDefinition, faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { SelectContent } from './_types';

interface CustomTrigger<T> extends BaseProps<T> {
  triggerEl: React.ReactNode;
}

interface StandardProps<T> extends BaseProps<T> {
  valueIcon?: IconDefinition;
  visibleValue?: string;
  placeholder?: string;
}

interface BaseProps<T> {
  value: string;
  disabled?: boolean;
  content: SelectContent<T>;
  onChange: (id: string) => void;
}

type Props<T> = StandardProps<T> | CustomTrigger<T>;

const isCustomTrigger = <T,>(props: Props<T>): props is CustomTrigger<T> => {
  return 'triggerEl' in props;
};

const DemmiSelect = <T = string,>(props: Props<T>): JSX.Element => {
  const CSSBlock = 'demmi-ui-select';
  const { content, disabled, value, onChange } = props;
  const customTrigger = isCustomTrigger<T>(props);

  const trigger = customTrigger ? (
    <Select.Trigger asChild>{props.triggerEl}</Select.Trigger>
  ) : (
    <Select.Trigger className={`demmi-ui-button ${CSSBlock}__trigger`}>
      {props?.valueIcon && (
        <span className={`${CSSBlock}__trigger__icon`}>
          <FontAwesomeIcon icon={props.valueIcon} />
        </span>
      )}
      <Select.Value
        aria-label={value}
        placeholder={props.placeholder ?? 'Select an item...'}>
        {props.visibleValue ?? value}
      </Select.Value>
      <span className={`${CSSBlock}__trigger__dropdown-icon`}>
        <FontAwesomeIcon icon={faCaretDown} />
      </span>
    </Select.Trigger>
  );

  const selectContent = (
    <Select.Content className="SelectContent">
      <Select.Viewport className="SelectViewport">
        {content.sections.map((section, i) => (
          <Select.Group key={i}>
            <Select.Label className="SelectLabel">
              {section.heading}
            </Select.Label>
            {section.items.map((item, j) => (
              <Select.Item key={j} value={item.id} className="SelectItem">
                <Select.ItemText>{item.text}</Select.ItemText>
              </Select.Item>
            ))}
          </Select.Group>
        ))}
      </Select.Viewport>
    </Select.Content>
  );

  return (
    <Select.Root value={value} onValueChange={onChange} disabled={disabled}>
      {trigger}
      {customTrigger ? (
        selectContent
      ) : (
        <Select.Portal>{selectContent}</Select.Portal>
      )}
    </Select.Root>
  );
};

export default DemmiSelect;
